import { useCallback, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Heading, Text } from 'react-aria-components';
import { AddPasskey, NicknameForm } from 'src/components/auth';
import PasskeyIcon from 'src/assets/icons/passkey.svg';
import CheckIcon from 'src/assets/icons/shield-check.svg';

import type { ClientWebAuthn } from 'src/types/client';

const AddPasskeyPage = () => {
  const [data, setData] = useState<null | ClientWebAuthn>(null);

  if (data?.clientWebauthnId) return <SuccessScreen data={data} />;

  return (
    <div className="bg-surface-alt text-surface-alt-90 rounded-3xl shadow-3 px-4 sm:px-6 pt-10 pb-6 mt-12">
      <div className="flex flex-col justify-center items-center mb-10">
        <PasskeyIcon className="w-14 h-14" />
        <Heading
          level={5}
          className="text-center text-surface-alt-90 text-3xl font-bold my-6"
        >
          Add a passkey
        </Heading>
      </div>
      <Text elementType="p">
        Your device supports passkeys, a password replacement that validates
        your identity using touch, facial, recognition, a password, or a PIN.
      </Text>
      <br />
      <Text elementType="p" className="mb-12">
        Passkeys enhance security by verifying the identity of users and
        protecting against unauthorized access.
      </Text>
      <AddPasskey onCompleted={(res: ClientWebAuthn) => setData(res)} />
    </div>
  );
};

const SuccessScreen = ({ data }: { data: ClientWebAuthn }) => {
  const navigate = useNavigate();

  const onDone = useCallback(
    () => navigate({ to: '/profile', hash: 'security', replace: true }),
    [navigate]
  );

  return (
    <div className="bg-surface-alt rounded-3xl shadow-3 px-4 sm:px-6 pt-10 pb-6 text-center mt-12">
      <CheckIcon className="inline-block w-14 h-14" />
      <Heading
        level={5}
        className="text-center text-surface-alt-90 text-3xl font-bold mt-6 mb-12"
      >
        Successfully added a passkey
      </Heading>
      <Text elementType="p" className="text-surface-alt-90 mb-2">
        Congratulations! Your passkey has been successfully created. Welcome to
        a realm where security meets seamless access.
      </Text>
      <NicknameForm
        id={data.clientWebauthnId}
        defaultValues={{ nickname: data.name || 'No Name' }}
        className="mx-auto my-8 text-left"
        onCompleted={onDone}
        buttonTitle="Done"
        buttonVariant="success"
      />
    </div>
  );
};

export const Route = createFileRoute('/_authenticated/_layout-basic/passkey/')({
  component: AddPasskeyPage,
});
